import React, { useContext, useMemo } from 'react';
import {
  ForEach,
  IconName,
  If,
  Menu,
  P,
  Stack,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import { Link, useLocation } from 'react-router-dom';
import { get } from 'lodash';
import cn from 'classnames';
import { NavigationUser } from './Props';
import { hasAccessTo } from '../../Lib/Constants/roles';
import { useNavigation } from '../../Lib/Hooks/navigation';
import {
  FeatureFlags,
  FeatureFlagsContext,
} from '../../Lib/Contexts/FeatureFlagsContext';

type Links = Array<{
  title?: string;
  items: Array<
    | {
        icon: IconName;
        label: string;
        link: string;
        badge?: string;
      }
    | undefined
  >;
}>;

type FilteredLinks = Array<{
  title?: string;
  items: Array<{
    icon: IconName;
    label: string;
    link: string;
    badge?: string;
  }>;
}>;

const getLinks = (
  currentUser: NavigationUser,
  user?: NavigationUser,
  applicationApproved?: boolean,
  flags?: FeatureFlags,
): Links => {
  const userId = user?.id || currentUser.id;
  const targetRole = user?.role || currentUser.role;
  const haveCounterAccess = user
    ? !!user.init_counter_email_sent_at
    : !!currentUser.init_counter_email_sent_at;
  const currentRole = currentUser.role;

  const hasInspoAccess =
    (flags?.new_inspo?.enabled &&
      hasAccessTo('users/:id/new-item-requests', currentRole)) ??
    false;

  switch (targetRole) {
    case 'ADMIN':
    case 'AP_ADMIN':
      return [
        {
          items: [
            {
              icon: 'building/building-apartment-two',
              label: 'Vendors',
              link: '/admin-console',
            },
            {
              icon: 'balance-scale-law',
              label: 'Compliance',
              link: '/compliance-console',
            },
            {
              icon: 'clipboard/clipboard-text',
              label: 'Onboarding Applications',
              link: '/vendor-applications',
            },
            {
              icon: 'building/building-apartment-big',
              label: 'Factories',
              link: '/compliance-factories',
            },
          ],
        },
        {
          title: 'Social Media',
          items: [
            {
              icon: 'camera',
              label: 'Influencers',
              link: '/social-media-console',
            },
            {
              icon: 'token',
              label: 'Tokens',
              link: '/social-media-console/invite-tokens',
            },
          ],
        },
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
            {
              icon: 'settings/settings-02',
              label: 'Manage',
              link: '/delivery-management',
            },
          ],
        },
        {
          items: [
            {
              icon: 'file-02/file-02-settings',
              label: 'File Manager',
              link: '/admin-console/public-files',
            },
            {
              icon: 'flag',
              label: 'Feature Flags',
              link: '/admin-console/feature-flags',
            },
            {
              icon: 'lock/lock-close',
              label: 'Permissions',
              link: '/admin-console/permissions',
            },
            currentUser?.feature_permissions?.dev_tools
              ? {
                  link: `/dev-tools`,
                  label: 'Dev Tools',
                  icon: 'chevron-big/chevron-big-right',
                }
              : undefined,
          ],
        },
      ];
    case 'EXECUTIVE':
      return [
        {
          items: [
            {
              icon: 'building/building-apartment-two',
              label: 'Vendors',
              link: '/admin-console',
            },
            {
              icon: 'clipboard/clipboard-text',
              label: 'Onboarding Applications',
              link: '/vendor-applications',
            },
          ],
        },
        {
          title: 'Social Media',
          items: [
            {
              icon: 'camera',
              label: 'Influencers',
              link: '/social-media-console',
            },
          ],
        },
        {
          items: [
            {
              icon: 'file-02/file-02-settings',
              label: 'File Manager',
              link: '/admin-console/public-files',
            },
          ],
        },
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
          ],
        },
      ];
    case 'SPONSOR':
      return [
        {
          items: [
            {
              icon: 'building/building-apartment-two',
              label: 'Vendors',
              link: '/admin-console',
            },
            {
              icon: 'clipboard/clipboard-text',
              label: 'Onboarding Applications',
              link: '/vendor-applications',
            },
          ],
        },
        {
          items: [
            {
              icon: 'file-02/file-02-settings',
              label: 'File Manager',
              link: '/admin-console/public-files',
            },
          ],
        },
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
          ],
        },
      ];
    case 'BUYER':
      return [
        {
          items: [
            {
              icon: 'building/building-apartment-two',
              label: 'Vendors',
              link: '/admin-console',
            },
            {
              icon: 'clipboard/clipboard-text',
              label: 'Onboarding Applications',
              link: '/vendor-applications',
            },
          ],
        },
        {
          items: [
            {
              icon: 'file-02/file-02-settings',
              label: 'File Manager',
              link: '/admin-console/public-files',
            },
          ],
        },
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
          ],
        },
      ];
    case 'COMPLIANCE':
      return [
        {
          items: [
            {
              icon: 'balance-scale-law',
              label: 'Compliance',
              link: '/compliance-console',
            },
            {
              icon: 'building/building-apartment-big',
              label: 'Factories',
              link: '/compliance-factories',
            },
          ],
        },
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
          ],
        },
        {
          items: [
            {
              icon: 'file-02/file-02-settings',
              label: 'File Manager',
              link: '/admin-console/public-files',
            },
          ],
        },
      ];
    case 'SCHEDULER':
      return [
        {
          items: [
            {
              icon: 'building/building-apartment-two',
              label: 'Vendors',
              link: '/admin-console',
            },
          ],
        },
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
            {
              icon: 'settings/settings-02',
              label: 'Manage',
              link: '/delivery-management',
            },
          ],
        },
        {
          items: [
            {
              icon: 'file-02/file-02-settings',
              label: 'File Manager',
              link: '/admin-console/public-files',
            },
          ],
        },
      ];
    case 'RECEIVER':
      return [
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
          ],
        },
      ];
    case 'SOCIAL_MEDIA_MANAGER':
      return [
        {
          title: 'Social Media',
          items: [
            {
              icon: 'camera',
              label: 'Influencers',
              link: '/social-media-console',
            },
            {
              icon: 'token',
              label: 'Tokens',
              link: '/social-media-console/invite-tokens',
            },
          ],
        },
      ];
    case 'SOCIAL_MEDIA_OPS':
      return [
        {
          title: 'Social Media',
          items: [
            {
              icon: 'camera',
              label: 'Influencers',
              link: '/social-media-console',
            },
          ],
        },
      ];
    case 'PRODUCT_DEVELOPMENT':
      return [
        {
          items: [
            {
              icon: 'building/building-apartment-two',
              label: 'Vendors',
              link: '/admin-console',
            },
            {
              icon: 'clipboard/clipboard-text',
              label: 'Onboarding Applications',
              link: '/vendor-applications',
            },
          ],
        },
        {
          title: 'Deliveries',
          items: [
            {
              icon: 'truck/truck-trash',
              label: 'Schedule',
              link: '/deliveries',
            },
          ],
        },
        {
          items: [
            {
              icon: 'file-02/file-02-settings',
              label: 'File Manager',
              link: '/admin-console/public-files',
            },
          ],
        },
      ];
    case 'VENDOR': {
      const defaultRoutes: Links = [
        {
          title: 'Settings',
          items: [
            hasAccessTo('users/:id/profile', currentRole)
              ? {
                  link: `/users/${userId}/profile`,
                  label: 'Profile',
                  icon: 'briefcase-job',
                }
              : undefined,
          ],
        },
      ];
      const approvedRoutes: Links = [
        {
          items: [
            hasAccessTo('users/:id/dashboard', currentRole)
              ? {
                  link: `/users/${userId}/dashboard`,
                  label: 'Dashboard',
                  icon: 'grid/grid-dashboard-02',
                }
              : undefined,
            flags?.vendor_catalog?.enabled &&
            hasAccessTo('users/:id/sell-to-us', currentRole)
              ? {
                  link: `/users/${userId}/sell-to-us`,
                  label: 'Sell To Us',
                  icon: 'star',
                }
              : undefined,
            flags?.sample_tags?.enabled &&
            hasAccessTo('users/:id/sample-tags', currentRole)
              ? {
                  link: `/users/${userId}/sample-tags`,
                  label: 'Sample Tags',
                  icon: 'tag',
                }
              : undefined,
          ],
        },
        {
          title: 'Requests',
          items: [
            hasInspoAccess
              ? {
                  label: 'New Item Requests',
                  link: `/users/${userId}/new-item-requests`,
                  icon: 'star',
                  badge: '2', // todo: Implement Inspo badge count
                }
              : undefined,
            hasAccessTo('users/:id/requested-items', currentRole)
              ? {
                  link: `/users/${userId}/requested-items`,
                  label: 'Item Requests',
                  icon: 'inbox/inbox-default',
                  badge: get(user, 'vendor.requested_item_lead_times_count'),
                }
              : undefined,
            haveCounterAccess &&
            hasAccessTo('users/:id/sample-requests', currentRole)
              ? {
                  link: `/users/${userId}/sample-requests`,
                  label: 'Sample Requests',
                  icon: 'inbox/inbox-default',
                }
              : undefined,
          ],
        },
        {
          title: 'POs',
          items: [
            hasAccessTo('users/:id/purchase-orders', currentRole)
              ? {
                  link: `/users/${userId}/purchase-orders`,
                  label: 'Purchase Orders',
                  icon: 'file/file-text',
                }
              : undefined,
            hasAccessTo('users/:id/deliveries', currentRole)
              ? {
                  link: `/users/${userId}/deliveries`,
                  label: 'Deliveries',
                  icon: 'truck/truck-default',
                }
              : undefined,
            hasAccessTo('users/:id/fn-catalog', currentRole) &&
            !(flags?.prop65 && flags.prop65?.enabled)
              ? {
                  link: `/users/${userId}/fn-catalog`,
                  label: 'Catalog',
                  icon: 'list/list-heart',
                }
              : undefined,
            {
              link: `/users/${userId}/printing-setup`,
              label: 'Printing Setup',
              icon: 'printer',
            },
          ],
        },
        {
          title: 'Payments',
          items: [
            hasAccessTo('users/:id/invoices', currentRole)
              ? {
                  link: `/users/${userId}/invoices`,
                  label: 'Invoices',
                  icon: 'bank',
                }
              : undefined,
            hasAccessTo('users/:id/credits', currentRole)
              ? {
                  link: `/users/${userId}/credits`,
                  label: 'Credits',
                  icon: 'credit-card/credit-card-arrow-left',
                }
              : undefined,
            hasAccessTo('users/:id/payments', currentRole)
              ? {
                  link: `/users/${userId}/payments`,
                  label: 'Payments',
                  icon: 'money-dollar-bag',
                }
              : undefined,
          ],
        },
        {
          title: 'Settings',
          items: [
            hasAccessTo('users/:id/profile', currentRole)
              ? {
                  link: `/users/${userId}/profile`,
                  label: 'Profile',
                  icon: 'briefcase-job',
                }
              : undefined,
            hasAccessTo('users/:id/team-members', currentRole)
              ? {
                  link: `/users/${userId}/team-members`,
                  label: 'Team Members',
                  icon: 'user/user-two',
                }
              : undefined,
            hasAccessTo('users/:id/factories', currentRole)
              ? {
                  link: `/users/${userId}/factories`,
                  label: 'Factories',
                  icon: 'building/building-apartment-big',
                }
              : undefined,
          ],
        },
      ];

      return applicationApproved ? approvedRoutes : defaultRoutes;
    }
    case 'VENDOR_STAFF': {
      return [
        {
          items: [
            flags?.vendor_catalog?.enabled &&
            hasAccessTo('users/:id/sell-to-us', currentRole)
              ? {
                  link: `/users/${userId}/sell-to-us`,
                  label: 'Sell To Us',
                  icon: 'star',
                }
              : undefined,
            flags?.sample_tags?.enabled &&
            hasAccessTo('users/:id/sample-tags', currentRole)
              ? {
                  link: `/users/${userId}/sample-tags`,
                  label: 'Sample Tags',
                  icon: 'tag',
                }
              : undefined,
          ],
        },
        {
          title: 'Requests',
          items: [
            hasInspoAccess
              ? {
                  label: 'New Item Requests',
                  link: `/users/${userId}/new-item-requests`,
                  icon: 'star',
                  badge: '2', // todo: Implement Inspo badge count
                }
              : undefined,
            hasAccessTo('users/:id/requested-items', currentRole)
              ? {
                  link: `/users/${userId}/requested-items`,
                  label: 'Item Requests',
                  icon: 'inbox/inbox-default',
                  badge: get(user, 'vendor.requested_item_lead_times_count'),
                }
              : undefined,
            haveCounterAccess &&
            hasAccessTo('users/:id/sample-requests', currentRole)
              ? {
                  link: `/users/${userId}/sample-requests`,
                  label: 'Sample Requests',
                  icon: 'inbox/inbox-default',
                }
              : undefined,
          ],
        },
        {
          title: 'POs',
          items: [
            hasAccessTo('users/:id/purchase-orders', currentRole)
              ? {
                  link: `/users/${userId}/purchase-orders`,
                  label: 'Purchase Orders',
                  icon: 'file/file-text',
                }
              : undefined,
            hasAccessTo('users/:id/deliveries', currentRole)
              ? {
                  link: `/users/${userId}/deliveries`,
                  label: 'Deliveries',
                  icon: 'truck/truck-default',
                }
              : undefined,
            !(flags?.prop65 && flags.prop65?.enabled) &&
            hasAccessTo('users/:id/fn-catalog', currentRole)
              ? {
                  link: `/users/${userId}/fn-catalog`,
                  label: 'Catalog',
                  icon: 'list/list-heart',
                }
              : undefined,
            {
              link: `/users/${userId}/printing-setup`,
              label: 'Printing Setup',
              icon: 'printer',
            },
          ],
        },
      ];
    }
    case 'VENDOR_OPERATIONS': {
      return [
        {
          items: [
            flags?.sample_tags?.enabled &&
            hasAccessTo('users/:id/sample-tags', currentRole)
              ? {
                  link: `/users/${userId}/sample-tags`,
                  label: 'Sample Tags',
                  icon: 'tag',
                }
              : undefined,
          ],
        },
        {
          title: 'POs',
          items: [
            hasAccessTo('users/:id/purchase-orders', currentRole)
              ? {
                  link: `/users/${userId}/purchase-orders`,
                  label: 'Purchase Orders',
                  icon: 'file/file-text',
                }
              : undefined,
            {
              link: `/users/${userId}/printing-setup`,
              label: 'Printing Setup',
              icon: 'printer',
            },
          ],
        },
      ];
    }
    case 'INFLUENCER':
      return [
        {
          items: [
            hasAccessTo('users/:id/influencer-payee-information', currentRole)
              ? {
                  link: `/users/${userId}/influencer-payee-information`,
                  label: 'Payee Information',
                  icon: 'safe',
                }
              : undefined,
            hasAccessTo('users/:id/invoices', currentRole)
              ? {
                  link: `/users/${userId}/invoices`,
                  label: 'Invoices',
                  icon: 'bank',
                }
              : undefined,
            hasAccessTo('users/:id/credits', currentRole)
              ? {
                  link: `/users/${userId}/credits`,
                  label: 'Credits',
                  icon: 'credit-card/credit-card-arrow-left',
                }
              : undefined,
            hasAccessTo('users/:id/payments', currentRole)
              ? {
                  link: `/users/${userId}/payments`,
                  label: 'Payments',
                  icon: 'money-dollar-bag',
                }
              : undefined,
          ],
        },
        {
          items: [
            hasAccessTo('users/:id/influencer-user-info', currentRole)
              ? {
                  link: `/users/${userId}/influencer-user-info`,
                  label: 'Profile',
                  icon: 'briefcase-job',
                }
              : undefined,
          ],
        },
      ];
    default:
      return [];
  }
};

const clearLinks = (links: Links): FilteredLinks =>
  links.reduce<FilteredLinks>((acc, group) => {
    const items = group.items.filter(Boolean) as Array<{
      icon: IconName;
      label: string;
      link: string;
      badge?: string;
    }>;
    if (items.length) {
      acc.push({ title: group.title, items });
    }
    return acc;
  }, []);

export function MaybeTooltip({
  condition,
  content,
  children,
}: {
  condition: boolean;
  content: string;
  children: React.ReactElement;
}) {
  return condition ? (
    <Tooltip arrow content={content} position="right" zIndex={10}>
      {children}
    </Tooltip>
  ) : (
    children
  );
}

export function NavigationLinks({
  currentUser,
  user,
  applicationApproved,
  closeMobilePopup,
}: {
  currentUser: NavigationUser;
  user?: NavigationUser;
  applicationApproved?: boolean;
  closeMobilePopup?: () => void;
}) {
  const { flags } = useContext(FeatureFlagsContext);
  const links = useMemo(
    () => clearLinks(getLinks(currentUser, user, applicationApproved, flags)),
    [currentUser, user, applicationApproved, flags],
  );
  const { pathname } = useLocation();
  const { isCollapsed } = useNavigation();

  return (
    <Stack direction="col" className="overflow-auto h-full py-4">
      <ForEach items={links}>
        {(group, index) => (
          <React.Fragment key={`${index}-${group.title}`}>
            <If condition={index}>
              <div className="py-4 px-5 w-full">
                <div className="self-stretch border-b border-neutral-200" />
              </div>
            </If>
            <Menu.Group title={isCollapsed ? '' : group.title}>
              <ForEach items={group.items}>
                {(item) => (
                  <MaybeTooltip
                    key={item.label}
                    condition={isCollapsed}
                    content={item.label}
                  >
                    <Link to={item.link} onClick={closeMobilePopup}>
                      <Menu.Item
                        icon={item.icon}
                        isActive={pathname === item.link}
                        className={cn({ 'justify-center': isCollapsed })}
                      >
                        {!isCollapsed && (
                          <span className="truncate">{item.label}</span>
                        )}
                        <If condition={item.badge}>
                          <P
                            color="disabled"
                            size="xs"
                            className="h-4 px-1 bg-neutral-200 rounded"
                          >
                            {item.badge}
                          </P>
                        </If>
                      </Menu.Item>
                    </Link>
                  </MaybeTooltip>
                )}
              </ForEach>
            </Menu.Group>
          </React.Fragment>
        )}
      </ForEach>
    </Stack>
  );
}
